import { useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useField, useFormikContext } from "formik"
import { Box, Divider, FormHelperText, Grid, MenuItem, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FormikDropdown, FormikSwitch, FormikTextField } from "../../../../../shared/components/forms/Formik"
import { sanitizeAndLimitInput } from "../../../../../shared/components/createOrder/components/form/helpers"
import { INVALID_INPUT_KEY } from "../../../../nonAdminUser/reports/settings/wallets/refill/components/WalletsRefillManual"
import { FormikImageUpload } from "../../../../../shared/components/Formik/FormikImageUpload"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { AvailabilityDetailsField } from "./AvailabilityDetailsField"
import { FormikProductGroupField } from "./FormikProductGroupField"
import { CreateUpdateProductFormValues, OfferDetailsType } from "../productsTypes"
import { FormikAllergensField } from "./FormikAllergensField"
import { DeadlineField, dayOptions } from "./DeadlineField"
import { FormikCurrencyField } from "./FormikCurrencyField"
import { OfferDetails } from "./offerDetails/OfferDetails"
// import { ItemLimitsField } from "./ItemLimitsField"
import { FormikPriceListField } from "./FormikPriceListsField"
import { StockField } from "./stockManagement/StockField"
import { InfoBox } from "../../shops/components/formElements/InfoBox"
import { MatchEnvironment } from "../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../environment/envTypes"

const useStyles = makeStyles()(() => ({
    hidePriceSwitch: {
        "& .MuiFormControl-root": {
            width: "10rem",
        },
    },
    generalRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    onSaleInput: {
        width: "5rem",
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
        "& .MuiInputBase-input": {
            textAlign: "right",
        },
    },
}))

type CreateUpdateProductFormContentProps = Readonly<{
    isEditMode?: boolean
}>

function OnSalePerDayField() {
    const { classes } = useStyles()
    const { submitCount } = useFormikContext()
    const [, meta] = useField("stock.itemsForSalePerDay")
    const showError = (meta.touched || submitCount > 0) && Boolean(meta.error)

    return (
        <Box>
            <Box className={classes.generalRow}>
                <LocalizedStrict id="product-stock-on-sale-per-day-label">
                    <Typography variant="body1">On sale per day:</Typography>
                </LocalizedStrict>
                <FormikTextField
                    name="stock.itemsForSalePerDay"
                    type="number"
                    className={classes.onSaleInput}
                    size="small"
                    FormHelperTextProps={{ style: { display: "none" } }}
                    inputProps={{
                        min: 0,
                        inputMode: "numeric" as const,
                        onInput: (e: React.FormEvent<HTMLInputElement>) => {
                            const sanitized = sanitizeAndLimitInput(e.currentTarget.value, 6)
                            if (e.currentTarget.value !== sanitized) {
                                e.currentTarget.value = sanitized
                            }
                        },
                        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (INVALID_INPUT_KEY.includes(e.key)) {
                                e.preventDefault()
                            }
                        },
                    }}
                />
            </Box>
            {showError && (
                <FormHelperText error sx={{ mt: 0 }}>
                    {meta.error}
                </FormHelperText>
            )}
        </Box>
    )
}

export function CreateUpdateProductFormContent({ isEditMode }: CreateUpdateProductFormContentProps) {
    const { classes } = useStyles()
    const { values, setFieldValue, errors } = useFormikContext<CreateUpdateProductFormValues>()
    const defaultTime = DateTime.fromISO("12:00")
    const priceListId = values.priceList?.id
    const isFirstRender = useRef(true)

    const [checkedDeadline, setCheckedDeadline] = useState(!!values?.deadline)
    const [checkedItemLimits, setCheckedItemLimits] = useState(!!values?.itemLimits)
    const [checkedOffer, setCheckedOffer] = useState(!!values?.offerDetails)
    const [offerDialogState, setOfferDialogState] = useState<boolean>(false)
    const [cachedDeadline, setCachedDeadline] = useState(values?.deadline)
    const [cachedItemLimits, setCachedItemLimits] = useState(values?.itemLimits)
    const [cachedOfferDetails, setCachedOfferDetails] = useState(values?.offerDetails)
    const handleChangeDeadline = (checked: boolean) => {
        setCheckedDeadline(checked)
        if (checked) {
            const restored = cachedDeadline ?? {
                purchases: { daysBefore: dayOptions[0].value, timeOfDay: defaultTime },
                // hide cancellation for now
                // cancellation: { daysBefore: dayOptions[0].value, timeOfDay: defaultTime },
            }
            setFieldValue("deadline", restored)
        } else {
            setCachedDeadline(values?.deadline)
            setFieldValue("deadline", null)
        }
    }
    const handleChangeItemLimits = (checked: boolean) => {
        setCheckedItemLimits(checked)
        if (checked) {
            const restored = cachedItemLimits ?? {
                maxDailyItemsPerPerson: "",
                minItemsPerOrder: "",
            }
            setFieldValue("itemLimits", restored)
        } else {
            setCachedItemLimits(values?.itemLimits)
            setFieldValue("itemLimits", null)
        }
    }
    const offerSavedRef = useRef(false)
    const handleChangeOffer = (checked: boolean) => {
        setCheckedOffer(checked)
        if (checked) {
            if (cachedOfferDetails) {
                setFieldValue("offerDetails", cachedOfferDetails)
            } else {
                offerSavedRef.current = false
                setOfferDialogState(true)
            }
        } else {
            setCachedOfferDetails(values?.offerDetails)
            setFieldValue("offerDetails", undefined)
        }
    }
    const handleOfferSave = (data: OfferDetailsType) => {
        offerSavedRef.current = true
        setFieldValue("offerDetails", data)
        setCheckedOffer(true)
    }
    const handleOfferDialogClose = () => {
        setOfferDialogState(false)
        if (!offerSavedRef.current) {
            setCheckedOffer(false)
        }
    }

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        setFieldValue("productGroup", null)
    }, [priceListId])

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    {/* <FormikPriceListsField name="priceLists" /> */}
                    <FormikPriceListField name="priceList" />

                    <FormikProductGroupField name="productGroup" priceListId={priceListId} />

                    <LocalizedStrict id="product-create-name-field-label" attrs={{ label: true }}>
                        <FormikTextField name="name" label="Product name" fullWidth />
                    </LocalizedStrict>

                    <Grid container item spacing={1} direction="row" alignItems="flex-start">
                        <Grid item sm={2.5}>
                            <LocalizedStrict id="product-create-currency-field-label" attrs={{ label: true }}>
                                <FormikDropdown name="currency" label="Currency" fullWidth>
                                    <MenuItem value="DKK">DKK</MenuItem>
                                </FormikDropdown>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item sm={3}>
                            <LocalizedStrict id="product-create-price-field-label" attrs={{ label: true }}>
                                <FormikCurrencyField name="price" label="Price" errorMessage={errors.price} />
                            </LocalizedStrict>
                        </Grid>
                        <Grid
                            item
                            sm
                            flexGrow={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            height="4rem"
                            className={classes.hidePriceSwitch}
                        >
                            <LocalizedStrict id="product-create-payment-price-switch">
                                <FormikSwitch name="paymentDetails.isPriceHidden">Hide price:</FormikSwitch>
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                </StandardMainPanel>
                <LocalizedStrict id="product-create-panel-description" attrs={{ title: true }}>
                    <StandardMainPanel title="Description">
                        <LocalizedStrict
                            id="product-create-product-description-marketing-tag-field"
                            attrs={{ label: true, helperText: true }}
                        >
                            <FormikTextField
                                name="productDescription.marketingTag"
                                label="Marketing tag"
                                helperText="Examples: 'Offer', 'CO2', 'Hot dish', 'Vegetarian'"
                                fullWidth
                            />
                        </LocalizedStrict>
                        <FormikAllergensField name="productDescription.allergens" />
                        <LocalizedStrict id="product-create-description-field-label" attrs={{ label: true }}>
                            <FormikTextField name="description" label="Description" rows={4} fullWidth multiline />
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-image-field-label" attrs={{ label: true }}>
                            <FormikImageUpload label="Image" name="productPhoto" typeOfEntity="product" />
                        </LocalizedStrict>
                    </StandardMainPanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-general-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="General" variant="collapsible">
                        <LocalizedStrict id="product-create-active-switch">
                            <FormikSwitch name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>

                        <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo, EnvironmentType.QA]}>
                            <Divider />
                            <OnSalePerDayField />
                            <LocalizedStrict id="product-stock-on-sale-per-day-info" attrs={{ description: true }}>
                                <InfoBox description="Using this you can limit the number of items on sale each day." />
                            </LocalizedStrict>
                            <Divider />
                            <StockField />
                        </MatchEnvironment>
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-offer-panel" attrs={{ title: true, offContent: true }}>
                    <StandardSidePanel
                        title="Offer"
                        variant="collapsibleWithToggle"
                        checked={checkedOffer}
                        onCheckChange={handleChangeOffer}
                        offContent="Offer not activated"
                    >
                        <OfferDetails
                            dialogState={offerDialogState}
                            setDialogState={setOfferDialogState}
                            setCheckedOffer={setCheckedOffer}
                            onDialogClose={handleOfferDialogClose}
                            onSaveOffer={handleOfferSave}
                        />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-availability-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Availability" variant="collapsible" expanded={false}>
                        <AvailabilityDetailsField />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-deadline-panel" attrs={{ title: true, offContent: true }}>
                    <StandardSidePanel
                        title="Deadline"
                        variant="collapsibleWithToggle"
                        checked={checkedDeadline}
                        onCheckChange={handleChangeDeadline}
                        offContent="Deadline not activated"
                    >
                        <DeadlineField />
                    </StandardSidePanel>
                </LocalizedStrict>

                {/* hide for now as we don't handle it in mobile and pos */}
                {/* <LocalizedStrict id="product-create-order-limits-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Order limits"
                        variant="collapsibleWithToggle"
                        checked={checkedItemLimits}
                        onCheckChange={handleChangeItemLimits}
                        offContent="Order limits not activated"
                    >
                        <ItemLimitsField />
                    </StandardSidePanel>
                </LocalizedStrict> */}
            </StandardForm>
        </>
    )
}
